import { useState } from "react";
import Dropdown from "components/Dropdown/Dropdown";
import {
  BlockIcon,
  CheckIcon,
  DislikeIcon,
  DropdownIcon,
  ICON_STROKE,
  LikeIcon,
  SeedIcon,
} from "shared/icons";

interface FeedbackOption {
  value: FeedbackType;
  label: string;
  Icon: typeof LikeIcon;
  tone: string;
}

/**
 * Same order and colours as SongActionBar, so a judgement looks the same
 * wherever it's set. Labels are states ("Liked"), not actions ("Like").
 */
const FEEDBACK_OPTIONS: FeedbackOption[] = [
  { value: "Seed", label: "Seed", Icon: SeedIcon, tone: "text-accent" },
  { value: "Like", label: "Liked", Icon: LikeIcon, tone: "text-spotify" },
  {
    value: "Do Not Play",
    label: "Do not play",
    Icon: BlockIcon,
    tone: "text-danger",
  },
  {
    value: "Dislike",
    label: "Disliked",
    Icon: DislikeIcon,
    tone: "text-ink-2",
  },
];

export interface FeedbackSelectProps {
  value: FeedbackType;
  songName: string;
  /** May return the save's promise; the pill shows the choice until it settles. */
  onChange: (value: FeedbackType) => unknown;
}

/** A track's feedback as a coloured pill, with a menu to change it. */
export function FeedbackSelect(props: FeedbackSelectProps): JSX.Element {
  const [pending, setPending] = useState<FeedbackType | null>(null);
  const current = pending ?? props.value;
  const shown = FEEDBACK_OPTIONS.find((o) => o.value === current);

  function choose(value: FeedbackType): void {
    if (value === current) {
      return;
    }
    setPending(value);
    // Once the save settles the store is the truth again: the new value if it
    // succeeded, the old one if it failed. A later choice owns `pending`.
    // `void`: deliberately not awaited, so the menu closes straight away.
    void Promise.resolve(props.onChange(value)).finally(() =>
      setPending((p) => (p === value ? null : p))
    );
  }

  return (
    <Dropdown
      label={`Feedback for ${props.songName}: ${shown?.label ?? "none"}`}
      menuLabel="Feedback"
      busy={pending !== null}
      triggerClassName={`inline-flex items-center gap-1.5 h-7 pl-2 pr-1.5 sm:w-32 rounded-pill border border-rule-strong bg-raised text-xs text-ink hover:border-muted transition-colors ${
        pending ? "opacity-60" : ""
      }`}
      trigger={
        <>
          {shown && (
            <shown.Icon
              size={14}
              strokeWidth={ICON_STROKE}
              className={`flex-none ${shown.tone}`}
              aria-hidden="true"
            />
          )}
          {/* Icon only on small screens; the colour still carries the state. */}
          <span className="hidden sm:inline truncate">
            {shown?.label ?? "Set feedback"}
          </span>
          <DropdownIcon
            size={14}
            strokeWidth={ICON_STROKE}
            className="flex-none ml-auto text-muted"
            aria-hidden="true"
          />
        </>
      }
    >
      {(close) =>
        FEEDBACK_OPTIONS.map(({ value, label, Icon, tone }) => {
          const checked = value === current;

          return (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={checked}
              className="menu-item"
              onClick={() => {
                close();
                choose(value);
              }}
            >
              <Icon
                size={16}
                strokeWidth={ICON_STROKE}
                className={`flex-none ${tone}`}
                aria-hidden="true"
              />
              <span className="flex-1">{label}</span>
              {checked && (
                <CheckIcon
                  size={15}
                  strokeWidth={ICON_STROKE}
                  className="flex-none text-ink-2"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })
      }
    </Dropdown>
  );
}

export default FeedbackSelect;
